import React from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowChevronArrow from './components/WorkflowChevronArrow';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'Steeringルール',
    title: 'Steeringルールの書き方を学ぶ',
    path: '/devdocs/handson/steering-rules',
  },
  { label: 'rfp作成', title: 'rfpを作成する', path: '/devdocs/handson/rfp' },
  { label: 'proposal/business', title: 'proposal/businessを生成する', active: true },
  { label: 'usecase', title: 'usecaseを生成する', path: '/devdocs/handson/usecase' },
  { label: 'product', title: 'productの機能一覧を生成する', path: '/devdocs/handson/product' },
];

const HandsonProposalBusiness: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="要件定義編 — proposal/business生成"
      seriesName="要件定義編"
      tags={['Hands-on', 'Proposal', 'Business', 'PDCA']}
      seriesSteps={seriesSteps}
      prevLink={{
        label: 'rfp作成',
        path: '/devdocs/handson/rfp',
      }}
      nextLink={{
        label: 'usecase生成',
        path: '/devdocs/handson/usecase',
      }}
    >
      <WorkflowGoalBox
        prerequisites={[
          '「要件定義編 — rfp作成」が完了している',
          '「要件定義編 — Steeringルールの書き方」のSteering知識（読み込みモード・テンプレート構造）を理解している',
        ]}
        completionCriteria={[
          '.kiro/steering/create_proposal.md が作成されている',
          '.kiro/steering/create_business.md が作成されている',
          '.kiro/requirements/todo/todo.proposal.md が生成されている',
          '.kiro/requirements/usecases/todo.business.md が生成されている',
          'セクション4のチェックリスト全項目に合格している',
          '少なくとも1回はルール改善→再生成のサイクルを経験している',
        ]}
      >
        Steeringルールの作成・改善を繰り返しながら、AIでproposal.md /
        business.mdを生成し、チェックリストに合格するまでPDCAを回す。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="要件定義編 — rfp作成"
        description="前の記事"
        path="/devdocs/handson/rfp"
      />

      <WorkflowLinkCard
        title="要件定義編 — Steeringルールの書き方"
        description="Steeringの基礎知識"
        path="/devdocs/handson/steering-rules"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'Steeringルールを作成する' },
          { id: 'section-3', label: 'AIと対話してproposal/businessを生成する' },
          { id: 'section-4', label: 'チェックリストで品質を確認する' },
          { id: 'section-5', label: 'ルールを改善して再生成する' },
          { id: 'section-6', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowSection title="PDCAサイクル — 「ルール→生成→チェック→改善」を回す" subNumber="1.1">
          <WorkflowText>
            「要件定義編 —
            Steeringルールの書き方」で「ルールの品質が出力の品質を決める」と学びました。この章ではそれを実体験します。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — Steeringルールの書き方"
            description="ルール改善PDCAの考え方（セクション6）"
            path="/devdocs/handson/steering-rules"
          />

          {/* 循環フロー図 */}
          <Grid container spacing={1} sx={{ maxWidth: 480, mx: 'auto', my: 2 }}>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Plan"
                sub="ルール作成"
                bgcolor="#1565c0"
                borderColor="#0d47a1"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Do"
                sub="AI生成"
                bgcolor="#2e7d32"
                borderColor="#1b5e20"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowUpwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={2} />
            <Grid size={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowDownwardIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Act"
                sub="ルール改善"
                bgcolor="#e65100"
                borderColor="#bf360c"
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid size={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ArrowForwardIcon
                sx={{ fontSize: 22, color: 'text.disabled', transform: 'rotate(180deg)' }}
              />
            </Grid>
            <Grid size={5}>
              <WorkflowChevronArrow
                label="Check"
                sub="品質確認"
                bgcolor="#f57f17"
                borderColor="#e65100"
                sx={{ height: '100%' }}
              />
            </Grid>
          </Grid>

          <WorkflowInfoBox type="info">
            <Typography variant="body2" fontWeight={600}>
              1回で完璧なものができる必要はありません。
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              むしろ、最初のルールでは不十分な生成物が出てくることが普通です。チェックリストで不合格箇所を見つけ、「なぜこうなったか」を分析し、ルールを改善して再生成する。このプロセスこそが学びです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="この章の流れ" subNumber="1.2">
          <WorkflowList
            variant="ordered"
            items={[
              'proposal/businessの役割を確認する',
              'Steeringルールを作成する（create_proposal.md / create_business.md）',
              'AIと対話してproposal/businessを生成する',
              'チェックリストで品質を確認する → 不合格箇所を特定',
              'ルールを改善して再生成する → 合格するまで繰り返す',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="参照する記事" subNumber="1.3">
          <WorkflowText>
            以下の紹介記事に、proposal/businessの詳細な説明・フォーマットの意図・対話の進め方・チェックリストが書かれています。この章を進める前に必ず読んでください。
          </WorkflowText>

          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — AI対話で仕様を策定する"
            description="AI対話の進め方・決定事項の蓄積方法・チェックリスト"
            path="/devdocs/workflow/req-step2"
          />
          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — proposal/businessを出力する"
            description="proposal/businessの構造・出力手順・提出前チェックリスト・修正指示の出し方"
            path="/devdocs/workflow/req-step3"
          />

          <WorkflowTipBox title="なぜ紹介記事を先に読むのか">
            <WorkflowText>
              このハンズオンは「体験すること」に集中しています。proposal/businessの意味・構造・なぜファイルを分けるのか等の知識面は紹介記事に詳しく書かれています。知識がないままルールを書いても「何を書けばいいか分からない」状態になるため、先に読んで全体像を掴んでおいてください。
            </WorkflowText>
            <WorkflowText>特に以下のセクションは重要です：</WorkflowText>
            <WorkflowList
              items={[
                'Step3 セクション1「概要」— proposal/businessの位置づけ',
                'Step3 セクション2「成果物1: proposal.md」— テンプレート構造',
                'Step3 セクション3「成果物2: business.md」— テンプレート構造',
                'Step3 セクション8「提出前チェックリスト」— この章のゴール基準',
              ]}
            />
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="Steeringルールを作成する" id="section-2" number={2}>
        <WorkflowSection title="何を作るか" subNumber="2.1">
          <WorkflowTable
            headers={['ファイル', '役割']}
            rows={[
              [
                '.kiro/steering/create_proposal.md',
                'proposal.md の生成ルール（テンプレート・記載ルール・チェックリスト）',
              ],
              [
                '.kiro/steering/create_business.md',
                'business.md の生成ルール（テンプレート・記載ルール・チェックリスト）',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ルールに書くべき情報" subNumber="2.2">
          <WorkflowTable
            headers={['順番', 'セクション', '書くこと']}
            rows={[
              ['1', '目的', 'このルールが何のために存在するか'],
              ['2', '成果物説明', '生成するファイルが何であるか'],
              ['3', '配置先', 'ファイルをどこに置くか'],
              ['4', '生成フロー', 'どういう手順で生成するか'],
              ['5', '確認事項', '生成前にAIが確認すべきこと'],
              ['6', 'テンプレート', '出力フォーマット'],
              ['7', '記載ルール', 'テンプレートの各項目をどう埋めるか'],
              ['8', 'チェックリスト', '生成後の品質確認項目'],
              ['9', '禁止事項', 'AIがやってはいけないこと'],
            ]}
          />

          <WorkflowTipBox title="読み込みモードの設定">
            <WorkflowText>
              この2ファイルは fileMatch モードで、rfp.md や proposal.md
              を開いたときに自動で読み込まれるようにします。Front Matter の globs
              にファイルパターンを指定します。
            </WorkflowText>
            <WorkflowCodeBlock
              language="yaml"
              code={`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.rfp.md"
  - ".kiro/requirements/**/*.proposal.md"
---`}
            />
            <WorkflowText>fileMatchPattern は非推奨です。globs を使ってください。</WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="2.3">
          <WorkflowText>以下の2ファイルを作成してください。</WorkflowText>
          <WorkflowList
            items={['.kiro/steering/create_proposal.md', '.kiro/steering/create_business.md']}
          />
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              紹介記事の例はKanzhong（中・大規模業務システム）向けです。TODOアプリ向けにはもっとシンプルで構いません。以下の点を意識してください：
            </Typography>
            <WorkflowList
              items={[
                '認証なし → 認証・認可セクションは不要、または「認証なし」と明記',
                'テーブル設計はシンプル → マスタテーブルの規約等は不要な場合が多い',
                'API設計は少数 → API IDの採番規則は簡素でよい',
                'フロントエンド仕様 → 画面ハンズオンが今後あるので、対話で決めてよい旨を書く',
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_proposal.md - 完成例" subNumber="2.4">
          <WorkflowLockedCodeBlock password="Xm4wRa">
            {`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.rfp.md"
  - ".kiro/requirements/**/*.proposal.md"
---

# proposal.md 生成ルール

## 目的

RFPを起点とした対話を通じて仕様を決定し、\`*.proposal.md\` を完成させるためのルール。

## 成果物説明

- proposal.md は「要件定義書」と「基本設計書」の中間に位置する技術仕様文書。
- テーブル設計、API設計、ビジネスルールの決定事項をまとめる。
- 後続の成果物（スキーマ定義、API仕様書、実装コード）の入力となる。

## 配置先

| 項目 | 内容 |
|------|------|
| 配置先 | \`.kiro/requirements/{機能名}/\` |
| ファイル名 | \`{機能名}.proposal.md\` |
| 例 | \`.kiro/requirements/todo/todo.proposal.md\` |

## 生成フロー

### RFPの読み方

- \`{機能名}.rfp.md\` は対話の出発点である。完成した仕様書ではない。
- RFP内の「回答」セクションがある場合、そこに書かれた内容は既に合意済みとして扱う。
- RFPの「ゴール」「進め方」セクションは対話の方針を示している。従う。

### 対話の順序

1. 機能の全体像 — エンティティの関係、主要なユースケースの確認
2. テーブル設計 — 必要なテーブル、カラム、型、制約
3. API設計 — 必要なAPI一覧、エンドポイント
4. API詳細 — 各APIのリクエスト/レスポンス、エラーケース
5. ビジネスルール — ステータス遷移、削除方式、採番等

### 書き出しのタイミング

- 「確認事項」の必須項目が全て合意済み
- ユーザーから「まとめて」等の指示がある、または全質問への回答が完了した
- 対話の中で未解決の論点がない

## 確認事項（必須）

### テーブル設計
- どのテーブルが必要か
- 各カラムの論理名・物理名・型・桁数
- NULL許可/不許可
- PK・UK・インデックスの設計

### API設計
- API ID（プロジェクト内で重複しないこと）
- エンドポイントのパス設計
- Method（POST / GET / PUT / DELETE）
- リクエスト・レスポンスの主要項目
- エラーケース

### ビジネスルール
- ステータス遷移（ある場合）
- 削除方式（論理削除 / 物理削除）
- 採番ルール（UUID / 連番）

## 記載ルール

### テーブル定義
- カラムは論理名・物理名・種別・Not Null・PK・備考の6列で記載する。
- 種別は varchar(n), int, datetime, tinyint(1), text, bigint 等を使用する。

### API概要
- エンドポイントは /v1/{リソース名}/{操作} の形式を基本とする。
- エラーケースは少なくとも「バリデーションエラー(400)」「対象なし(404)」を検討する。

### 数値の扱い
- 桁数・ID体系は勝手に決めない。必ずユーザーに確認する。

## チェックリスト

- [ ] 「確認事項」の必須項目が全て合意済みで記載されているか
- [ ] テーブル定義の全カラムに論理名・物理名・種別・Not Null・PKが記載されているか
- [ ] API一覧に全APIが含まれ、IDが重複していないか
- [ ] API概要に各APIのリクエスト・レスポンス・エラーが記載されているか
- [ ] ビジネスルールが対話で合意した内容と整合しているか
- [ ] RFPの「やりたいこと」に記載された要件が全てカバーされているか

## 禁止事項

- 合意を得ていない項目を proposal.md に書き出さない。
- 数値（桁数、ID等）を勝手に決定しない。
- Not Null の設定を勝手に決めない。任意入力項目を確認なしにNot Nullにしない。
- RFPで既に回答済みの内容を再質問しない。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>

        <WorkflowSection title="create_business.md - 完成例" subNumber="2.5">
          <WorkflowLockedCodeBlock password="Tn9bYc">
            {`---
inclusion: fileMatch
globs:
  - ".kiro/requirements/**/*.rfp.md"
  - ".kiro/requirements/**/*.proposal.md"
  - ".kiro/requirements/usecases/*.business.md"
---

# business.md 生成ルール

## 目的

RFPを起点とした対話の中で、ユーザー視点の利用シナリオ（ビジネスユースケース）を決定し、
\`*.business.md\` として構造化して出力するためのルール。

## 成果物説明

- business.md は「ユーザーがシステムをどう使うか」のシナリオ文書。
- 画面チームが操作手順書を作成するための元情報。

## 配置先

| 項目 | 内容 |
|------|------|
| 配置先 | \`.kiro/requirements/usecases/\` |
| ファイル名 | \`{機能名}.business.md\` |
| 例 | \`.kiro/requirements/usecases/todo.business.md\` |

## 生成フロー

- proposal.md のAPI一覧・API概要・ビジネスルールを元にユースケースを導出する。
- proposal.md の内容は確定済みとして扱う。矛盾する内容を書かない。

## 確認事項

1. ユースケースの洗い出し（全量の確認）
2. 各ユースケースの操作者
3. 画面フロー（どの画面からどの順序で操作するか）
4. 入力項目（必須/任意、形式、桁数）
5. 注意事項（不可逆操作の警告等）

## 記載ルール

### ユーザー目線で書く
- 技術用語（API, DB, Entity）は手順内で使わない。
- 「画面」「ボタン」「入力」「選択」「表示」で記述する。

### 入力項目テーブル
- 桁数は proposal.md のテーブル定義の種別（varchar(n)のn）と一致させる。
- 必須/任意は proposal.md の Not Null と一致させる。
- ステータスや区分値はユーザー向け日本語で記述し、備考列にproposalの英字コードを記載する。

### 利用API
- API ID・Endpointは proposal.md のAPI一覧と一致させる。

### 手順
- 最初のステップは「{画面名}を開く」から始める。
- 入力→確認→実行の流れで最低3ステップ以上とする。

## チェックリスト

- [ ] proposal.md の全APIが少なくとも1つのBUCの「利用API」に含まれているか
- [ ] 入力項目の桁数・必須/任意が proposal.md と整合しているか
- [ ] BUC-IDの連番が重複していないか
- [ ] 各BUCの手順が画面操作として自然な流れになっているか
- [ ] RFPの「やりたいこと」に記載された要件が全てカバーされているか

## 禁止事項

- proposal.md に記載のないAPIやフローを勝手に追加しない。
- 入力項目の桁数・必須/任意を proposal.md と矛盾させない。
- 技術用語を手順内に書かない。
- 対話で合意していない画面フローや操作手順を推測で書かない。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="AIと対話してproposal/businessを生成する" id="section-3" number={3}>
        <WorkflowSection title="対話を開始する" subNumber="3.1">
          <WorkflowText>rfp.mdをコンテキストに含めて、AIと対話を開始してください。</WorkflowText>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: AIからフロントエンドの構成について聞かれたら、REST API +
              SPA（TypeScript /
              React）と答えてください。Thymeleaf等のサーバーサイドレンダリングは選ばないでください。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              AIからフロントエンドのプロジェクト配置を聞かれたら別プロジェクト（別リポジトリ）と答えてください。
            </Typography>
          </WorkflowInfoBox>

          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — AI対話で仕様を策定する"
            description="AI対話の進め方・対話パターン・決定事項の蓄積方法"
            path="/devdocs/workflow/req-step2"
          />
        </WorkflowSection>

        <WorkflowSection title="proposal/businessの書き出しを指示する" subNumber="3.2">
          <WorkflowText>
            対話が十分に進んだら、proposal/businessの生成を指示してください。指示方法（一括生成/段階的生成）は紹介記事のセクション4「出力手順」を参照してください。
          </WorkflowText>

          <WorkflowLinkCard
            title="AIと作る、壊れないバックエンド紹介記事 — proposal/businessを出力する"
            description="一括生成/段階的生成のプロンプト例・修正指示の出し方"
            path="/devdocs/workflow/req-step3"
          />

          <WorkflowInfoBox type="info">
            <Typography variant="body2">
              一括生成・段階的生成のどちらを使っても構いません。大事なのは生成後にセクション4のチェックを必ず行うことです。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-4" number={4}>
        <WorkflowSection title="チェックの基準" subNumber="4.1">
          <WorkflowTable
            headers={['チェックリスト', '記載場所', '確認観点']}
            rows={[
              [
                '対話の完了チェック',
                '紹介記事 Step2「決定すべき事項チェックリスト」',
                'テーブル/API/ビジネスルール等が全て決まっているか',
              ],
              [
                '生成物の品質チェック',
                '紹介記事 Step3 セクション8「提出前チェックリスト」',
                'proposal/businessの記載内容が正しいか',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="不合格パターンと改善ケーススタディ" subNumber="4.2">
          <WorkflowText>
            チェックリストで不合格が出たら、まず原因を切り分けて対処方針を決めます。
          </WorkflowText>
          <WorkflowTable
            headers={['分類', '対処']}
            rows={[
              ['[対話不足] 決定事項そのものが足りない', 'セクション3に戻って対話を続ける'],
              ['[ルール不足] ルールの記載が甘くてAIが間違える', 'セクション5でルールを改善する'],
              ['[部分ミス] 生成物の一部が間違っている', 'AIに修正指示を出す'],
            ]}
          />

          <WorkflowText>
            以下は [ルール不足]
            に該当するケーススタディです。チェック不合格の症状から、ルールのどこを直すべきかを逆引きできます。
          </WorkflowText>

          <WorkflowTable
            headers={['#', '症状', '原因', '改善']}
            rows={[
              [
                '1',
                '入力項目テーブルの桁数が全て「-」',
                '「どこを参照して桁数を決めるか」がない',
                '記載ルールに桁数の参照先を追記',
              ],
              [
                '2',
                'API概要にエラーケースがない',
                '何を書くべきか具体的にない',
                '記載ルールにエラーケースの最低限を追記',
              ],
              [
                '3',
                '手順が1行で終わっている',
                '最低ステップ数の指定がない',
                '最低3ステップ以上と追記',
              ],
              [
                '4',
                'Not Null列が全部Yes',
                'AIがデフォルトでYesにしている',
                '禁止事項に「確認なしにNot Nullにしない」追記',
              ],
              [
                '5',
                'proposalとbusinessでEndpointが食い違い',
                '生成時にproposal.mdをコンテキストに含めていない',
                '指示方法の改善: 明示的にファイルを渡す',
              ],
              [
                '6',
                'ステータスの表記揺れ',
                '日本語化ルールとの対応関係が不明確',
                '備考列にproposalの英字コードを記載するルール追加',
              ],
            ]}
          />

          <WorkflowTipBox title="ケーススタディの使い方">
            <WorkflowText>チェックリストで不合格が出たら：</WorkflowText>
            <WorkflowList
              variant="ordered"
              items={[
                '上の表から似た症状を探す',
                '「原因」列を読んで、自分のルールの同じ箇所を確認する',
                '「改善」列を参考に、自分のSteeringファイルを修正する',
                '再生成してチェックを通す',
              ]}
            />
            <WorkflowText>
              ここに載っていないパターンが出た場合は、同じ思考プロセス（症状→原因→改善）で自分で分析してください。それ自体が学びです。
            </WorkflowText>
          </WorkflowTipBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="ルールを改善して再生成する" id="section-5" number={5}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600}>
            重要: このセクションは完了条件の1つです。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            1回目の生成でチェックリストに全合格しても、意図的にルールを1箇所削って再生成し、品質が下がることを確認してください。「ルールの有無で品質が変わる」ことを体感するのがこの章の目的です。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="なぜルール改善が必要か" subNumber="5.1">
          <WorkflowTable
            headers={['アプローチ', 'やること', '結果']}
            rows={[
              [
                'プロンプトで修正指示',
                '「桁数を一致させて」と指示して今回の成果物を直す',
                '今回は直る。次回また同じ問題が出る',
              ],
              [
                'ルールを改善する',
                'Steeringに「桁数の参照先はproposal.mdのテーブル定義」と追記する',
                '今回も次回以降も同じ品質で生成される',
              ],
            ]}
          />
          <WorkflowText>
            成果物を直すだけでなく、なぜその問題が起きたかを分析してルールに反映する。これが品質を安定させるための根本改善です。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="改善の手順" subNumber="5.2">
          <WorkflowText>
            Step 1: 不合格の原因を特定する — 「何が足りなかったか」を具体的に言語化します。
          </WorkflowText>
          <WorkflowText>Step 2: ルールのどこを直すか決める:</WorkflowText>
          <WorkflowTable
            headers={['不合格の原因', '直すべき場所']}
            rows={[
              ['出力に項目が抜けている', 'テンプレートに項目を追加する'],
              ['出力のフォーマットがバラバラ', 'テンプレートを具体化する'],
              ['出力の値が間違っている', '記載ルールに制約を追加する'],
              ['やってはいけないことをやっている', '禁止事項に追加する'],
              ['そもそも確認されていなかった', '確認事項に追加する'],
            ]}
          />
          <WorkflowText>Step 3: Steeringファイルを修正する。</WorkflowText>
          <WorkflowText>Step 4: 再生成する:</WorkflowText>
          <WorkflowChatBubble role="human">
            {`create_proposal.md / create_business.md のルールを更新しました。
更新したルールに従って、todo.proposal.md / todo.business.md を再生成してください。`}
          </WorkflowChatBubble>
          <WorkflowText>
            Step 5: 再度チェックする — 合格するまでStep 1〜5を繰り返してください。
          </WorkflowText>

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>:
              ルールを改善しすぎて複雑になるのもアンチパターンです。ルールは「最小限の制約で最大限の品質を得る」ことを目指してください。5回以上のサイクルを回してもチェックが通らない場合は、rfp.mdの内容を見直した方がよいかもしれません。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-6" number={6}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [
                        { name: 'todo.rfp.md' },
                        { name: 'todo.proposal.md', desc: '今回生成', highlight: '#1976d2' },
                      ],
                    },
                    {
                      name: 'usecases',
                      children: [
                        { name: 'todo.business.md', desc: '今回生成', highlight: '#1976d2' },
                      ],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md' },
                    { name: 'create_proposal.md', desc: '今回作成', highlight: '#e65100' },
                    { name: 'create_business.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                      ],
                    },
                    { name: 'resources', children: [{ name: 'application.yml' }] },
                  ],
                },
                { name: 'test', children: [] },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 次のステップ */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          次の記事
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          要件定義編 — usecase生成
        </Typography>
        <WorkflowList
          items={[
            'usecase生成用のSteeringルール（create_usecases.md）を作成する',
            'proposal.md + business.mdを入力としてusecases.mdを生成する',
            '同じPDCAサイクルでチェックリスト合格を目指す',
          ]}
        />
      </WorkflowInfoBox>

      <WorkflowLinkCard
        title="要件定義編 — usecase生成"
        description="usecase生成用のSteeringルールを作成し、proposal.md + business.mdを入力としてusecases.mdを生成する"
        path="/devdocs/handson/usecase"
      />

      {/* チェックリスト */}
      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/create_proposal.md が作成されている',
              '.kiro/steering/create_business.md が作成されている',
              '.kiro/requirements/todo/todo.proposal.md が生成されている',
              '.kiro/requirements/usecases/todo.business.md が生成されている',
              'セクション4のチェックリスト全項目に合格している',
              '少なくとも1回はルール改善→再生成のサイクルを経験した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonProposalBusiness;
